import { create } from 'zustand';
import axios from 'axios';
import { toast } from 'sonner';
import { getBackendBaseUrl } from '../lib/backendUrl';
import { useAuthStore } from './authStore';

const API = `${getBackendBaseUrl()}/api/advisor`;

function getUserId() {
  return useAuthStore.getState().user?.id || 'default';
}

function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

export const useAdvisorStore = create((set, get) => ({
  clients: [],
  clientsLoading: false,
  clientsError: null,
  activeOrgId: null,

  activeClient: null,
  activeClientLoading: false,
  activeClientError: null,

  modelPortfolios: [],
  modelPortfoliosLoading: false,

  proposals: [],
  proposalsLoading: false,

  rebalanceResult: null,
  rebalanceLoading: false,

  riskResult: null,
  riskLoading: false,

  suitability: null,
  suitabilityLoading: false,

  pendingProposals: [],
  pendingProposalsLoading: false,
  mandate: null,
  reviews: [],
  attribution: null,
  attributionLoading: false,
  portalLink: null,

  setActiveOrgId: (orgId) => set({ activeOrgId: orgId || null }),

  // ── Clients ────────────────────────────────────────────
  fetchClients: async () => {
    set({ clientsLoading: true, clientsError: null });
    try {
      const params = { page: 1, page_size: 100 };
      const oid = get().activeOrgId;
      if (oid) params.org_id = oid;
      const { data } = await axios.get(`${API}/clients`, { params });
      set({ clients: asList(data), clientsLoading: false });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load clients';
      set({ clientsError: msg, clientsLoading: false });
    }
  },

  createClient: async (client) => {
    try {
      const oid = get().activeOrgId;
      const { data } = await axios.post(`${API}/clients`, {
        ...client,
        advisor_user_id: getUserId(),
        ...(oid ? { org_id: oid } : {}),
      });
      set(s => ({ clients: [{ ...client, ...data, holdings_count: 0, proposal_count: 0 }, ...s.clients] }));
      toast.success('Client created');
      return data;
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Could not create client';
      toast.error(msg);
      throw e;
    }
  },

  fetchClient: async (clientId) => {
    set({ activeClientLoading: true, activeClientError: null });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}`);
      set({ activeClient: data, activeClientLoading: false, activeClientError: null });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Failed to load client';
      set({ activeClient: null, activeClientLoading: false, activeClientError: msg });
    }
  },

  updateClient: async (clientId, updates) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, updates);
      set(s => ({
        clients: s.clients.map(c => c.id === clientId ? { ...c, ...updates } : c),
        activeClient: s.activeClient?.id === clientId ? { ...s.activeClient, ...updates } : s.activeClient,
      }));
      toast.success('Client updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Update failed');
      throw e;
    }
  },

  deleteClient: async (clientId) => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      set(s => ({ clients: s.clients.filter(c => c.id !== clientId) }));
      toast.success('Client removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Delete failed');
      throw e;
    }
  },

  // ── Risk Profile ───────────────────────────────────────
  submitRiskProfile: async (clientId, answers) => {
    set({ riskLoading: true });
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/risk-profile`, { answers });
      set(s => ({
        riskResult: data,
        riskLoading: false,
        activeClient: s.activeClient?.id === clientId
          ? { ...s.activeClient, risk_score: data.risk_score, risk_bucket: data.risk_bucket }
          : s.activeClient,
      }));
      toast.success('Risk profile saved');
      return data;
    } catch (e) {
      set({ riskLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Risk profile failed');
      throw e;
    }
  },

  fetchSuitability: async (clientId) => {
    set({ suitabilityLoading: true });
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/suitability`);
      set({ suitability: data, suitabilityLoading: false });
    } catch (e) {
      set({ suitabilityLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Suitability check failed');
    }
  },

  // ── Model Portfolios ──────────────────────────────────
  fetchModelPortfolios: async () => {
    set({ modelPortfoliosLoading: true });
    try {
      const { data } = await axios.get(`${API}/model-portfolios`, { params: { page: 1, page_size: 100 } });
      set({ modelPortfolios: asList(data), modelPortfoliosLoading: false });
    } catch (e) {
      set({ modelPortfoliosLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Failed to load model portfolios');
    }
  },

  createModelPortfolio: async (mp) => {
    try {
      const { data } = await axios.post(`${API}/model-portfolios`, { ...mp, advisor_user_id: getUserId() });
      set(s => ({ modelPortfolios: [{ ...mp, ...data }, ...s.modelPortfolios] }));
      toast.success('Model portfolio created');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Create failed');
      throw e;
    }
  },

  updateModelPortfolio: async (mpId, updates) => {
    try {
      await axios.put(`${API}/model-portfolios/${mpId}`, updates);
      set(s => ({
        modelPortfolios: s.modelPortfolios.map(m => m.id === mpId ? { ...m, ...updates } : m),
      }));
      toast.success('Model portfolio updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Update failed');
      throw e;
    }
  },

  deleteModelPortfolio: async (mpId) => {
    try {
      await axios.delete(`${API}/model-portfolios/${mpId}`);
      set(s => ({ modelPortfolios: s.modelPortfolios.filter(m => m.id !== mpId) }));
      toast.success('Model portfolio deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Delete failed');
      throw e;
    }
  },

  // ── Proposals ─────────────────────────────────────────
  generateProposal: async (clientId, { modelPortfolioId, corpus } = {}) => {
    set({ proposalsLoading: true });
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/proposals/generate`, {
        advisor_user_id: getUserId(),
        model_portfolio_id: modelPortfolioId || null,
        corpus: corpus || null,
      });
      set(s => ({
        proposals: [data, ...s.proposals],
        proposalsLoading: false,
      }));
      toast.success('Proposal generated');
      return data;
    } catch (e) {
      set({ proposalsLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Proposal generation failed');
      throw e;
    }
  },

  fetchProposals: async (clientId) => {
    set({ proposalsLoading: true });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/proposals`, { params: { page: 1, page_size: 100 } });
      set({ proposals: asList(data), proposalsLoading: false });
    } catch (e) {
      set({ proposalsLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Failed to load proposals');
    }
  },

  updateProposalStatus: async (proposalId, status) => {
    try {
      const { data } = await axios.put(`${API}/proposals/${proposalId}/status`, { status });
      set(s => ({
        proposals: s.proposals.map(p => p.id === proposalId ? { ...p, status } : p),
      }));
      const msg = data.holdings_added > 0
        ? `Proposal accepted — ${data.holdings_added} stocks added to portfolio`
        : 'Proposal status updated';
      toast.success(msg);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Update failed');
      throw e;
    }
  },

  // ── Rebalancing ───────────────────────────────────────
  rebalanceClient: async (clientId, modelPortfolioId) => {
    set({ rebalanceLoading: true });
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/rebalance`, {
        advisor_user_id: getUserId(),
        model_portfolio_id: modelPortfolioId || null,
      });
      set({ rebalanceResult: data, rebalanceLoading: false });
      toast.success('Rebalance analysis ready');
      return data;
    } catch (e) {
      set({ rebalanceLoading: false });
      toast.error(e.response?.data?.detail || e.message || 'Rebalance failed');
      throw e;
    }
  },

  clearActiveClient: () => set({
    activeClient: null,
    activeClientError: null,
    proposals: [],
    rebalanceResult: null,
    riskResult: null,
    suitability: null,
    mandate: null,
    reviews: [],
    attribution: null,
    attributionLoading: false,
    portalLink: null,
  }),

  fetchPendingProposals: async (orgId) => {
    set({ pendingProposalsLoading: true });
    try {
      const { data } = await axios.get(`${API}/proposals/pending`, { params: { org_id: orgId } });
      set({ pendingProposals: Array.isArray(data) ? data : [], pendingProposalsLoading: false });
    } catch {
      set({ pendingProposals: [], pendingProposalsLoading: false });
    }
  },

  submitProposalReview: async (proposalId) => {
    await axios.post(`${API}/proposals/${proposalId}/submit-review`);
    toast.success('Submitted for compliance review');
  },

  approveProposal: async (proposalId) => {
    await axios.post(`${API}/proposals/${proposalId}/approve`);
    toast.success('Proposal approved');
  },

  rejectProposal: async (proposalId, notes) => {
    await axios.post(`${API}/proposals/${proposalId}/reject`, { review_notes: notes || '' });
    toast.success('Proposal rejected');
  },

  fetchMandate: async (clientId) => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/mandate`);
      set({ mandate: data.mandate || null });
    } catch {
      set({ mandate: null });
    }
  },

  saveMandate: async (clientId, payload) => {
    await axios.put(`${API}/clients/${clientId}/mandate`, payload);
    toast.success('Mandate saved');
    await get().fetchMandate(clientId);
  },

  enablePortal: async (clientId) => {
    const { data } = await axios.post(`${API}/clients/${clientId}/portal/enable`);
    const base = window.location.origin;
    set({ portalLink: `${base}/public/client-portal/${data.portal_token}` });
    toast.success('Portal enabled');
    return data;
  },

  disablePortal: async (clientId) => {
    await axios.post(`${API}/clients/${clientId}/portal/disable`);
    set({ portalLink: null });
    toast.success('Portal disabled');
  },

  fetchReviews: async (clientId) => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/reviews`);
      set({ reviews: Array.isArray(data) ? data : [] });
    } catch {
      set({ reviews: [] });
    }
  },

  generateReview: async (clientId, period) => {
    const { data } = await axios.post(
      `${API}/clients/${clientId}/reviews/generate`,
      null,
      { params: { period: period || 'Current quarter' } },
    );
    toast.success('Review draft generated');
    return data;
  },

  sendReview: async (reviewId) => {
    await axios.post(`${API}/reviews/${reviewId}/send`);
    toast.success('Review marked sent');
  },

  fetchAttribution: async (clientId, period = '3m') => {
    set({ attributionLoading: true });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/attribution`, { params: { period } });
      set({ attribution: data, attributionLoading: false });
    } catch {
      set({ attribution: null, attributionLoading: false });
    }
  },

  fetchModelPerformance: async (mpId, period = '1y') => {
    const { data } = await axios.get(`${API}/model-portfolios/${mpId}/performance`, { params: { period } });
    return data;
  },

  // ── Client Holdings ──
  clientHoldings: [],
  clientHoldingsLoading: false,

  fetchClientHoldings: async (clientId) => {
    set({ clientHoldingsLoading: true });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/holdings`);
      set({ clientHoldings: data.holdings || [], clientHoldingsLoading: false });
    } catch {
      set({ clientHoldings: [], clientHoldingsLoading: false });
    }
  },

  // ── Daily Intelligence Reports ──
  dailyReport: null,
  dailyReportLoading: false,
  dailyReports: [],
  dailyReportsLoading: false,

  fetchLatestDailyReport: async (clientId) => {
    set({ dailyReportLoading: true });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/daily-reports/latest`);
      set({ dailyReport: data.report, dailyReportLoading: false });
    } catch {
      set({ dailyReport: null, dailyReportLoading: false });
    }
  },

  fetchDailyReports: async (clientId) => {
    set({ dailyReportsLoading: true });
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/daily-reports`, { params: { page: 1, page_size: 30 } });
      set({ dailyReports: data.items || [], dailyReportsLoading: false });
    } catch {
      set({ dailyReports: [], dailyReportsLoading: false });
    }
  },

  generateDailyReport: async (clientId) => {
    set({ dailyReportLoading: true });
    try {
      const { data } = await axios.post(`${API}/clients/${clientId}/daily-reports/generate`);
      set({ dailyReport: { content_json: data.content_json, content_md: data.content_md, report_date: data.report_date, status: data.status }, dailyReportLoading: false });
      toast.success('Daily intelligence report generated');
      return data;
    } catch (e) {
      set({ dailyReportLoading: false });
      toast.error(e.response?.data?.detail || 'Failed to generate report');
      return null;
    }
  },
}));
